import {
  createApi,
  type FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import { waServicesQuery } from './baseQueries';

export interface ExternalVendorKey {
  ExternalVendorId: number;
  Name?: string;
  IsIntergrated?: boolean;
  IsEnabled?: boolean;
  ExternalUserGuid?: string | null;
  Code?: string;
}

// MoneyGuide Advisor Institution mapping model per server contract
export interface MgpAdvisorInstitutionMapModel {
  InstitutionId?: number;
  IsReadOnly?: boolean;
  HasSSOed?: boolean;
}

export const integrationsApi = createApi({
  reducerPath: 'integrationsApi',
  baseQuery: waServicesQuery,
  tagTypes: ['ExternalVendorKeys', 'MgpInstitutionMap'],
  endpoints: (builder) => ({
    getAdvisorExternalVendorKeys: builder.query<ExternalVendorKey[], void>({
      queryFn: async (_arg, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: `/api/advisor/externalVendorKeys`,
          method: 'GET',
        });
        if (result.error) return { error: result.error as FetchBaseQueryError };
        return { data: result.data as ExternalVendorKey[] };
      },
      providesTags: (_res) => [{ type: 'ExternalVendorKeys', id: 'LIST' }],
    }),
    // Legacy trigger used elsewhere; server returns a single model
    // Keep for compatibility with existing call sites that don't use the data
    getMgpAdvisorInstitutionMaps: builder.query<
      MgpAdvisorInstitutionMapModel | null,
      void
    >({
      queryFn: async (_arg, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: `/api/advisor/MGPAdvisorInstitutionMaps`,
          method: 'GET',
        });
        if (result.error) return { error: result.error as FetchBaseQueryError };
        return { data: (result.data as MgpAdvisorInstitutionMapModel) ?? null };
      },
    }),

    // Accurate, typed endpoints for MoneyGuide institution map CRUD
    getMgpAdvisorInstitutionMap: builder.query<
      MgpAdvisorInstitutionMapModel | null,
      void
    >({
      queryFn: async (_arg, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: `/api/advisor/MGPAdvisorInstitutionMaps`,
          method: 'GET',
        });
        if (result.error) return { error: result.error as FetchBaseQueryError };
        return { data: (result.data as MgpAdvisorInstitutionMapModel) ?? null };
      },
      providesTags: (_res) => [{ type: 'MgpInstitutionMap', id: 'SINGLE' }],
    }),
    createMgpAdvisorInstitutionMap: builder.mutation<
      MgpAdvisorInstitutionMapModel,
      { InstitutionId: number | string }
    >({
      queryFn: async (body, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: `/api/advisor/MGPAdvisorInstitutionMaps`,
          method: 'POST',
          body,
        });
        if (result.error) return { error: result.error as FetchBaseQueryError };
        return { data: result.data as MgpAdvisorInstitutionMapModel };
      },
      invalidatesTags: [{ type: 'MgpInstitutionMap', id: 'SINGLE' }],
    }),
    updateMgpAdvisorInstitutionMap: builder.mutation<
      boolean,
      { InstitutionId: number }
    >({
      queryFn: async (body, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: `/api/advisor/MGPAdvisorInstitutionMaps`,
          method: 'PUT',
          body,
        });
        if (result.error) return { error: result.error as FetchBaseQueryError };
        return { data: Boolean(result.data) };
      },
      invalidatesTags: [{ type: 'MgpInstitutionMap', id: 'SINGLE' }],
    }),
    updateExternalVendorKey: builder.mutation<
      ExternalVendorKey,
      { id: number; body: ExternalVendorKey }
    >({
      queryFn: async ({ id, body }, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: `/api/advisor/externalVendorKeys/${id}`,
          method: 'PUT',
          body,
        });
        if (result.error) return { error: result.error as FetchBaseQueryError };
        return { data: result.data as ExternalVendorKey };
      },
      invalidatesTags: [{ type: 'ExternalVendorKeys', id: 'LIST' }],
    }),
  }),
});

export const {
  useGetAdvisorExternalVendorKeysQuery,
  useLazyGetAdvisorExternalVendorKeysQuery,
  useGetMgpAdvisorInstitutionMapsQuery,
  useLazyGetMgpAdvisorInstitutionMapsQuery,
  useGetMgpAdvisorInstitutionMapQuery,
  useCreateMgpAdvisorInstitutionMapMutation,
  useUpdateMgpAdvisorInstitutionMapMutation,
  useUpdateExternalVendorKeyMutation,
} = integrationsApi;
